/**
 * Type Definitions for NodeBeat2
 *
 * This file contains all the TypeScript interfaces and types used throughout
 * the application. Centralizing types here makes them easy to find and
 * ensures consistency across the codebase.
 *
 * TYPESCRIPT BASICS:
 * ==================
 * - `interface`: Defines the shape of an object
 * - `type`: Can define object shapes, unions, or aliases
 * - `?`: Optional property (may be undefined)
 * - `|`: Union type (can be one of several types)
 */

// =============================================================================
// BEAT NODE TYPES
// =============================================================================

/**
 * Camera movement types for overlays
 */
export type CameraMovementType =
  | 'pan-left'
  | 'pan-right'
  | 'tilt-up'
  | 'tilt-down'
  | 'zoom-in'
  | 'zoom-out'
  | 'push-in'
  | 'pull-out'
  | 'truck-left'
  | 'truck-right'
  | 'boom-up'
  | 'boom-down'
  | 'frame';

/**
 * Preset position types for overlay graphics
 */
export type OverlayPresetPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'left-center'
  | 'center'
  | 'right-center'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

/**
 * Camera overlay definition
 *
 * Overlays are visual indicators placed on storyboard images to show
 * intended camera movements (pan, tilt, zoom, dolly, etc.)
 */
export interface CameraOverlay {
  /** Unique ID for this overlay instance */
  id: string;

  /** Type of camera movement */
  type: CameraMovementType;

  /** Position on image (normalized 0-1 coordinates) */
  position: {
    /** Horizontal position (0 = left edge, 1 = right edge) */
    x: number;
    /** Vertical position (0 = top edge, 1 = bottom edge) */
    y: number;
  };

  /** Preset position (optional, overrides x/y if set) */
  presetPosition?: OverlayPresetPosition;

  /** Rotation in degrees (0-360), default 0 */
  rotation?: number;

  /** Scale factor (1 = default size), default 1 */
  scale?: number;

  /** Opacity (0-1), default 0.8 */
  opacity?: number;

  /** Custom color (hex), default uses overlay default */
  color?: string;

  /** Custom image path for image-based overlays (relative to overlay assets folder) */
  imagePath?: string;

  /** For zoom overlays: frame position (0-1 normalized) */
  framePosition?: { x: number; y: number };

  /** For zoom overlays: frame size (0-1 normalized, relative to container) */
  frameSize?: number;
}

// =============================================================================
// TEXT OVERLAY TYPES
// =============================================================================

/**
 * Font family options for text overlays
 */
export type TextFontFamily =
  | 'Arial'
  | 'Helvetica'
  | 'Times New Roman'
  | 'Georgia'
  | 'Courier New'
  | 'Verdana'
  | 'Impact';

/**
 * Text alignment options for text overlays
 */
export type TextAlignment = 'left' | 'center' | 'right';

/**
 * Text overlay definition
 *
 * Text overlays are used for titles, subtitles, and captions on storyboard images.
 * Supports YouTube Shorts captions with large text and custom positioning.
 */
export interface TextOverlay {
  /** Unique ID for this overlay instance */
  id: string;

  /** The text content to display */
  text: string;

  /** Position on image (normalized 0-1 coordinates) */
  position: {
    /** Horizontal position (0 = left edge, 1 = right edge) */
    x: number;
    /** Vertical position (0 = top edge, 1 = bottom edge) */
    y: number;
  };

  /** Preset position (optional, overrides x/y if set) */
  presetPosition?: OverlayPresetPosition;

  /** Font family */
  fontFamily?: TextFontFamily;

  /** Font size in pixels (scales with container width) */
  fontSize?: number;

  /** Text color (hex) */
  color?: string;

  /** Text alignment */
  alignment?: TextAlignment;

  /** Opacity (0-1), default 1 */
  opacity?: number;

  /** Font weight (normal, bold, etc.) */
  fontWeight?: string;

  /** Font style (normal, italic, etc.) */
  fontStyle?: string;
}

// =============================================================================
// TITLE NODE TYPES
// =============================================================================

/**
 * Title node data
 *
 * Represents a title/subtitle block that exists independently from beat images.
 * Used for separate title layer in timeline.
 */
export interface TitleNodeData {
  /** Display title for this title block */
  title: string;

  /** The actual text content to display */
  text: string;

  /** Font family */
  fontFamily?: TextFontFamily;

  /** Font size in pixels (scales with container width) */
  fontSize?: number;

  /** Text color (hex) */
  color?: string;

  /** Text alignment */
  alignment?: TextAlignment;

  /** Position on image (normalized 0-1 coordinates) */
  position: {
    /** Horizontal position (0 = left edge, 1 = right edge) */
    x: number;
    /** Vertical position (0 = top edge, 1 = bottom edge) */
    y: number;
  };

  /** Preset position (optional, overrides x/y if set) */
  presetPosition?: OverlayPresetPosition;

  /** Font weight (normal, bold, etc.) */
  fontWeight?: string;

  /** Font style (normal, italic, etc.) */
  fontStyle?: string;

  /** Opacity (0-1), default 1 */
  opacity?: number;

  /** Duration in seconds */
  duration: number;

  /** Start time in timeline (seconds) */
  startTime: number;

  /** Index signature for React Flow compatibility */
  [key: string]: unknown;
}

/**
 * A title node in the timeline
 *
 * Similar to beat nodes but without image data, used for separate title layer.
 */
export interface TitleNode {
  /** Unique identifier */
  id: string;

  /** Node type - always 'title' */
  type: 'title';

  /** Position in the timeline editor */
  position: {
    x: number;
    y: number;
  };

  /** The title data */
  data: TitleNodeData;

  /** Whether the title node is currently selected */
  selected?: boolean;
}

/**
 * Default values for a new title node
 */
export const DEFAULT_TITLE_DATA: Omit<TitleNodeData, 'startTime' | 'duration'> = {
  title: 'New Title',
  text: 'Title Text',
  fontFamily: 'Arial',
  fontSize: 48,
  color: '#FFFFFF',
  alignment: 'center',
  position: { x: 0.5, y: 0.5 },
  presetPosition: 'center',
  fontWeight: 'bold',
  fontStyle: 'normal',
  opacity: 1,
};

/**
 * Represents a single story beat in the node editor
 *
 * Each beat contains:
 * - Script text (the story content)
 * - Optional image (storyboard frame)
 * - Duration (how long this beat plays)
 * - Position in the editor (x determines sequence order)
 *
 * Note: The index signature [key: string]: unknown is required for
 * compatibility with React Flow v12's strict typing.
 */
export interface BeatNodeData {
  /** Display title for the node */
  title: string;

  /** Optional scene heading (e.g., "INT. LOCATION - DAY") */
  sceneHeading: string;

  /** The script/story content in Markdown/Fountain format */
  script: string;

  /** Relative path to the image file (relative to project folder) */
  imagePath: string | null;

  /** Duration of this beat in seconds */
  duration: number;

  /** Image horizontal offset for cropping (0 = centered, -1 to 1 range) */
  imageOffsetX?: number;

  /** Image vertical offset for cropping (0 = centered, -1 to 1 range) */
  imageOffsetY?: number;

  /** Image scale/zoom (1 = 100%, range: 0.5 to 2) */
  imageScale?: number;

  /** Whether this beat is on the shelf (excluded from timeline) */
  onShelf?: boolean;

  /** Stored position before moving to shelf (for restoration) */
  shelfStoredPosition?: { x: number; y: number };

  /** Custom color for the node (optional, uses theme default if not set) */
  color?: string;

  /** Camera overlays applied to this beat's image */
  overlays?: CameraOverlay[];

  /** Text overlays for titles, subtitles, and captions */
  textOverlays?: TextOverlay[];

  /** Index signature for React Flow compatibility */
  [key: string]: unknown;
}

/**
 * A beat node as stored in the project file
 *
 * This extends React Flow's node structure with our custom data.
 * The `position.x` value determines the order in the timeline.
 */
export interface BeatNode {
  /** Unique identifier for this node */
  id: string;

  /** Node type - always 'beat' for our beat nodes */
  type: 'beat';

  /** Position in the node editor canvas */
  position: {
    /** Horizontal position - determines sequence order */
    x: number;
    /** Vertical position - for visual organization only */
    y: number;
  };

  /** The beat's content data */
  data: BeatNodeData;

  /** Whether the node is currently selected */
  selected?: boolean;
}

// =============================================================================
// REFERENCE NODE TYPES
// =============================================================================

/**
 * Reference image data for the reference library
 * Similar to BeatNodeData but simplified - no script, duration, or overlays
 */
export interface ReferenceNodeData {
  /** Display title/name for the reference */
  title: string;

  /** Notes/description about this reference */
  notes: string;

  /** Tags/categories for organization (array of strings) */
  tags: string[];

  /** Path to the reference image (managed by blobManager) */
  imagePath: string | null;

  /** Image offset X for cropping (reuse existing pattern from BeatNode) */
  imageOffsetX?: number;

  /** Image offset Y for cropping */
  imageOffsetY?: number;

  /** Image scale/zoom (1 = 100%, range: 0.5 to 2) */
  imageScale?: number;

  /** Custom color for the node (optional) */
  color?: string;

  /** Natural image width (for adaptive sizing calculation) */
  imageWidth?: number;

  /** Natural image height (for adaptive sizing calculation) */
  imageHeight?: number;

  /** Calculated node display width (adaptive to image aspect ratio) */
  nodeWidth?: number;

  /** Calculated node display height (adaptive to image aspect ratio) */
  nodeHeight?: number;

  /** User-overridden width (manual resize, takes precedence over nodeWidth) */
  userWidth?: number;

  /** User-overridden height (manual resize, takes precedence over nodeHeight) */
  userHeight?: number;

  /** Index signature for React Flow compatibility */
  [key: string]: unknown;
}

/**
 * A reference node as stored in the project file
 * Follows same structure as BeatNode for consistency
 */
export interface ReferenceNode {
  /** Unique identifier */
  id: string;

  /** Node type - always 'reference' */
  type: 'reference';

  /** Position in the reference library canvas */
  position: {
    x: number;
    y: number;
  };

  /** The reference data */
  data: ReferenceNodeData;

  /** Whether the node is currently selected */
  selected?: boolean;
}

/**
 * Default values for new reference node
 */
export const DEFAULT_REFERENCE_DATA: ReferenceNodeData = {
  title: 'New Reference',
  notes: '',
  tags: [],
  imagePath: null,
};

// =============================================================================
// NOTE NODE TYPES
// =============================================================================

/**
 * Data stored in a note node
 *
 * Note nodes are resizable text boxes with markdown support for annotations
 * and documentation in the reference library.
 */
export interface NoteNodeData {
  /** Title of the note */
  title: string;

  /** Markdown content */
  content: string;

  /** Custom border color (optional) */
  color?: string;

  /** Whether to render markdown (true) or show plain text (false) */
  renderMarkdown: boolean;

  /** Index signature for React Flow compatibility */
  [key: string]: unknown;
}

/**
 * A note node as stored in the project file
 */
export interface NoteNode {
  /** Unique identifier */
  id: string;

  /** Node type - always 'note' */
  type: 'note';

  /** Position in the reference library canvas */
  position: {
    x: number;
    y: number;
  };

  /** User-controlled width (resizable) */
  width?: number;

  /** User-controlled height (resizable) */
  height?: number;

  /** The note data */
  data: NoteNodeData;

  /** Whether the node is currently selected */
  selected?: boolean;

  /** Z-index for stacking order */
  zIndex?: number;
}

/**
 * Default values for new note node
 */
export const DEFAULT_NOTE_DATA: NoteNodeData = {
  title: 'New Note',
  content: '',
  color: undefined,
  renderMarkdown: true,
};

// =============================================================================
// PROJECT TYPES
// =============================================================================

/**
 * Video aspect ratio options
 */
export type AspectRatio = '16:9' | '9:16' | '1:1';

/**
 * Aspect ratio dimensions (width x height)
 */
export interface AspectRatioDimensions {
  width: number;
  height: number;
  ratio: number; // width / height
}

/**
 * Aspect ratio configurations
 */
export const ASPECT_RATIOS: Record<AspectRatio, AspectRatioDimensions> = {
  '16:9': { width: 1920, height: 1080, ratio: 16 / 9 },
  '9:16': { width: 1080, height: 1920, ratio: 9 / 16 },
  '1:1': { width: 1080, height: 1080, ratio: 1 },
};

/**
 * The complete project structure saved to disk
 *
 * This is serialized to JSON when saving and parsed when loading.
 */
export interface Project {
  /** Project file format version (for future compatibility) */
  version: string;

  /** User-defined project name */
  name: string;

  /** Frames per second for timeline calculations */
  fps: number;

  /** Video aspect ratio for output */
  aspectRatio: AspectRatio;

  /** All beat nodes in the project */
  nodes: BeatNode[];

  /** Reference image nodes (optional for backwards compatibility) */
  referenceNodes?: ReferenceNode[];

  /** Note nodes (optional for backwards compatibility) */
  noteNodes?: NoteNode[];

  /** Title nodes for separate title layer (optional for backwards compatibility) */
  titleNodes?: TitleNode[];

  /** Audio tracks for background music/voiceover (optional for backwards compatibility) */
  audioTracks?: AudioTrack[];

  /** Comment boxes (optional, for backwards compatibility) */
  commentBoxes?: CommentBoxNode[];

  /** Scene heading history for autocomplete (optional for backwards compatibility) */
  sceneHeadingHistory?: string[];

  /** Tag history for reference autocomplete (optional for backwards compatibility) */
  tagHistory?: string[];

  /** Author of the project (optional, for PDF export) */
  author?: string;

  /** Project description (optional, for PDF export) */
  description?: string;

  /** When the project was created */
  createdAt: string;

  /** When the project was last modified */
  modifiedAt: string;
}

/**
 * Default values for a new project
 */
export const DEFAULT_PROJECT: Omit<Project, 'createdAt' | 'modifiedAt'> = {
  version: '1.0',
  name: 'Untitled Project',
  fps: 24,
  aspectRatio: '16:9',
  nodes: [],
  referenceNodes: [],
  noteNodes: [],
  titleNodes: [],
  audioTracks: [],
  commentBoxes: [],
  sceneHeadingHistory: [],
  tagHistory: [],
  author: '',
  description: '',
};

/**
 * Default values for a new beat node
 */
export const DEFAULT_BEAT_DATA: BeatNodeData = {
  title: 'New Beat',
  sceneHeading: '',
  script: '',
  imagePath: null,
  duration: 3, // 3 seconds default
  onShelf: false,
};

// =============================================================================
// AUDIO TRACK TYPES
// =============================================================================

/**
 * Audio track data for background music or voiceover
 */
export interface AudioTrack {
  /** Unique identifier for this audio track */
  id: string;

  /** Display name for the track */
  name: string;

  /** Audio file data as base64 or blob URL */
  audioData: string;

  /** Start time in timeline (seconds) */
  startTime: number;

  /** Duration of the audio in seconds */
  duration: number;

  /** Volume level (0-1, default 1) */
  volume: number;

  /** Whether the track is muted */
  muted: boolean;

  /** Whether the track is solo (only this track plays) */
  solo: boolean;

  /** Original file name for reference */
  fileName: string;

  /** File type (e.g., 'audio/mpeg', 'audio/wav') */
  fileType: string;

  /** Trim start time - seconds to skip from beginning of audio file */
  trimStart: number;

  /** Trim end time - seconds to cut from end of audio file */
  trimEnd: number;

  /** Waveform data for visualization (optional, generated on import) */
  waveform?: {
    /** Sampled amplitude values (0-255, where 128 is silence) as base64 string */
    samples: string;
    /** Number of samples per second of audio */
    samplesPerSecond: number;
    /** Duration of the audio when waveform was generated */
    duration: number;
  };
}

/**
 * Default values for a new audio track
 */
export const DEFAULT_AUDIO_TRACK: Omit<AudioTrack, 'id' | 'audioData' | 'duration' | 'fileName' | 'fileType'> = {
  name: 'Audio Track',
  startTime: 0,
  volume: 1,
  muted: false,
  solo: false,
  trimStart: 0,
  trimEnd: 0,
};

// =============================================================================
// PLAYBACK TYPES
// =============================================================================

/**
 * Playback state for the timeline
 */
export interface PlaybackState {
  /** Whether the timeline is currently playing */
  isPlaying: boolean;

  /** Current playhead position in seconds */
  currentTime: number;

  /** Total duration of all beats combined */
  totalDuration: number;
}

// =============================================================================
// UI TYPES
// =============================================================================

/**
 * Panel layout sizes (for resizable panels)
 */
export interface PanelSizes {
  /** Width of the left panel (Output + Script) as percentage */
  leftPanelWidth: number;

  /** Height of the output window as percentage of left panel */
  outputHeight: number;

  /** Height of the timeline as pixels */
  timelineHeight: number;
}

/**
 * ResizeHandle Component Props
 */
export interface ResizeHandleProps {
  /** Direction of resize (vertical for left/right, horizontal for up/down) */
  direction: 'vertical' | 'horizontal';

  /** Callback fired when resize occurs, receives pixel delta */
  onResize: (delta: number) => void;

  /** Optional callback when resize starts */
  onResizeStart?: () => void;

  /** Optional callback when resize ends */
  onResizeEnd?: () => void;

  /** Optional inline styles */
  style?: React.CSSProperties;
}

// =============================================================================
// ELECTRON API TYPES
// =============================================================================

/**
 * Type declaration for window.electronAPI
 *
 * This must match the API exposed in electron/preload.ts
 * It allows TypeScript to know what functions are available.
 */
export interface ElectronAPI {
  readFile: (filePath: string) => Promise<{
    success: boolean;
    content?: string;
    error?: string;
  }>;

  writeFile: (
    filePath: string,
    content: string
  ) => Promise<{
    success: boolean;
    error?: string;
  }>;

  copyImage: (
    sourcePath: string,
    projectDir: string
  ) => Promise<{
    success: boolean;
    relativePath?: string;
    absolutePath?: string;
    error?: string;
  }>;

  fileExists: (filePath: string) => Promise<boolean>;

  selectImage: () => Promise<{
    success: boolean;
    filePath?: string;
    canceled?: boolean;
    error?: string;
  }>;

  selectProjectDir: () => Promise<{
    success: boolean;
    filePath?: string;
    canceled?: boolean;
    error?: string;
  }>;

  onMenuEvent: (
    event:
      | 'new-project'
      | 'open-project'
      | 'save-project'
      | 'save-project-as'
      | 'toggle-playback'
      | 'go-to-start'
      | 'go-to-end',
    callback: (data?: string) => void
  ) => () => void;
}

// Extend the Window interface to include our Electron API
declare global {
  interface Window {
    electronAPI?: ElectronAPI;
  }
}

// =============================================================================
// COMMENT BOX TYPES
// =============================================================================

/**
 * Comment box data (stored inside React Flow node)
 */
export interface CommentBoxData {
  /** User-defined title/label */
  title: string;

  /** Border/background color */
  color: string;

  /** IDs of nodes contained in this box */
  nodeIds: string[];

  /** Optional notes and thoughts about this comment box */
  notes?: string;

  /** Callbacks for updates and deletion */
  onUpdate?: (boxId: string, updates: Partial<Omit<CommentBoxData, 'onUpdate' | 'onDelete'>>) => void;
  onDelete?: (boxId: string) => void;

  /** Index signature for React Flow compatibility */
  [key: string]: unknown;
}

/**
 * A comment box node (React Flow node with comment data)
 */
export interface CommentBoxNode {
  /** Unique identifier */
  id: string;

  /** Node type - always 'commentBox' */
  type: 'commentBox';

  /** Position in the node editor */
  position: {
    x: number;
    y: number;
  };

  /** Size of the comment box */
  width?: number;
  height?: number;

  /** The comment box data */
  data: CommentBoxData;

  /** Whether the box is selected */
  selected?: boolean;

  /** z-index to render behind beat nodes */
  zIndex?: number;
}

/**
 * Default values for a new comment box
 */
export const DEFAULT_COMMENT_BOX_DATA: Omit<CommentBoxData, 'onUpdate' | 'onDelete'> = {
  title: 'Comment',
  color: '#E47019', // Orange brand color
  nodeIds: [],
  notes: '',
};

// =============================================================================
// UTILITY TYPES
// =============================================================================

/**
 * Represents a beat with its calculated timeline position
 * Used when computing which beat is active at a given time
 */
export interface TimelineBeat {
  /** The beat node data */
  node: BeatNode;

  /** Start time in seconds */
  startTime: number;

  /** End time in seconds */
  endTime: number;

  /** Index in the sorted sequence */
  index: number;
}
